import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import type { MaterialUse, StockStatus, StockStatusItem } from '../../types/factory';
import { FlatNavContext, Kpi, KpiStrip, LEVEL_CHIP, MaterialSelect, PageHeader, ProductSelect, StatusChip, errorMessage, fmtQty, num, usePageView } from './shared';
import type { PageView } from './shared';

const LEVEL_ORDER: Record<string, number> = { rojo: 0, amarillo: 1, 'sin-configurar': 2, verde: 3 };

const VIEWS: PageView[] = [
  { key: 'terminado', label: 'Stock de producto terminado' },
  { key: 'mp', label: 'Stock de materia prima' },
  { key: 'mp-por-producto', label: 'Qué MP usa cada producto', description: 'Cuántos kg de cada materia prima lleva una unidad: con eso se calcula para cuántas unidades alcanza la MP.' },
];

/**
 * Semáforo de stock para programar qué producir y qué comprar: verde por encima del aviso, amarillo en el
 * aviso o por debajo del mínimo con unidades, rojo sin stock.
 */
const FactoryStockPage: React.FC = () => {
  const { tab, setTab, showTabs, flat, current } = usePageView(VIEWS);
  const [data, setData] = useState<StockStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.getStockStatus());
    } catch (e) {
      setError(errorMessage(e, 'No se pudo cargar el stock'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const count = (items: StockStatusItem[], level: string) => items.filter(i => i.level === level).length;

  return (
    <Box>
      <PageHeader
        title={flat ? current.label : 'Stock y alertas'}
        description={flat && current.description ? current.description : <><b>Verde</b>: por encima del aviso. <b>Amarillo</b>: llegó al aviso o está bajo el mínimo, pero hay unidades. <b>Rojo</b>: sin stock.
        La producción sugerida completa hasta el máximo (o dos veces el aviso) más lo que falta entregar de OC abiertas.</>}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!data ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : (
        <>
          <KpiStrip>
            <Kpi label="Terminados en rojo" value={count(data.products, 'rojo')} color="#d32f2f" />
            <Kpi label="Terminados en amarillo" value={count(data.products, 'amarillo')} color="#ed6c02" />
            <Kpi label="Insumos en rojo" value={count(data.materials, 'rojo')} color="#d32f2f" />
            <Kpi label="Insumos en amarillo" value={count(data.materials, 'amarillo')} color="#ed6c02" />
          </KpiStrip>
          {showTabs && (<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label={`Productos terminados (${data.products.length})`} />
            <Tab label={`Materias primas (${data.materials.length})`} />
            <Tab label="MP por producto" />
          </Tabs>)}
          {tab === 0 && <ProductsTab items={data.products} reload={load} />}
          {tab === 1 && <MaterialsTab items={data.materials} reload={load} />}
          {tab === 2 && <UsesTab onChange={load} />}
        </>
      )}
    </Box>
  );
};

// ==================== Terminados ====================

const ProductsTab: React.FC<{ items: StockStatusItem[]; reload: () => void }> = ({ items, reload }) => {
  const navigate = useNavigate();
  const flat = useContext(FlatNavContext);
  const [filter, setFilter] = useState<'alertas' | 'todos'>('alertas');
  const [selected, setSelected] = useState<number[]>([]);
  const [editing, setEditing] = useState<StockStatusItem | null>(null);

  const rows = useMemo(() => [...items]
    .filter(i => filter === 'todos' || i.level === 'rojo' || i.level === 'amarillo' || i.suggestedQuantity > 0)
    .sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level] || a.name.localeCompare(b.name)), [items, filter]);

  const plan = () => {
    const lines = items.filter(i => selected.includes(i.id)).map(i => ({ productId: i.id, quantity: i.suggestedQuantity || i.batchSize || 1 }));
    navigate(flat ? '/factory/production/planificar' : '/factory/production?tab=plan', { state: { lines } });
  };

  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <ToggleButtonGroup exclusive size="small" value={filter} onChange={(_, v) => v && setFilter(v)}>
          <ToggleButton value="alertas">Para producir</ToggleButton>
          <ToggleButton value="todos">Todos</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" disabled={selected.length === 0} onClick={plan}>Planificar seleccionados ({selected.length})</Button>
      </Stack>
      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Estado</TableCell>
                <TableCell>Producto</TableCell>
                <TableCell align="right">Stock</TableCell>
                <TableCell align="right">Mínimo</TableCell>
                <TableCell align="right">Aviso</TableCell>
                <TableCell align="right">Máximo</TableCell>
                <TableCell align="right">OC por entregar</TableCell>
                <TableCell align="right">Con la MP alcanza para</TableCell>
                <TableCell align="right">Producir</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>Nada para producir: todo en verde.</TableCell></TableRow>}
              {rows.map(i => (
                <TableRow key={i.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={selected.includes(i.id)} onChange={e => setSelected(e.target.checked ? [...selected, i.id] : selected.filter(x => x !== i.id))} />
                  </TableCell>
                  <TableCell><StatusChip map={LEVEL_CHIP} value={i.level} /></TableCell>
                  <TableCell>{i.name}{!i.hasRecipe && <Typography variant="caption" color="text.secondary" display="block">sin formulación</Typography>}</TableCell>
                  <TableCell align="right"><b>{fmtQty(i.stock)}</b></TableCell>
                  <TableCell align="right">{fmtQty(i.minStock)}</TableCell>
                  <TableCell align="right">{fmtQty(i.warningLevel)}</TableCell>
                  <TableCell align="right">{i.maxStock ? fmtQty(i.maxStock) : '—'}</TableCell>
                  <TableCell align="right">{i.pendingOrders ? fmtQty(i.pendingOrders) : '—'}</TableCell>
                  <TableCell align="right">
                    {i.capacity ? (
                      <Tooltip title={i.capacity.components.map(c => `${c.component || 'Total'}: ${fmtQty(c.units)} u. (${c.materials.join(', ')})`).join(' · ')}>
                        <span>{fmtQty(i.capacity.units)} u.</span>
                      </Tooltip>
                    ) : '—'}
                  </TableCell>
                  <TableCell align="right">{i.suggestedQuantity > 0 ? <b>{fmtQty(i.suggestedQuantity)}</b> : '—'}</TableCell>
                  <TableCell align="right"><IconButton size="small" onClick={() => setEditing(i)}><Edit fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      {editing && <LevelsDialog item={editing} kind="product" onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </>
  );
};

// ==================== Materias primas ====================

const MaterialsTab: React.FC<{ items: StockStatusItem[]; reload: () => void }> = ({ items, reload }) => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<number[]>([]);
  const [editing, setEditing] = useState<StockStatusItem | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rows = [...items].sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level] || a.name.localeCompare(b.name));

  const request = async () => {
    try {
      await api.createPurchaseRequest({
        priority: 'Media',
        notes: 'Insumos en alerta (tablero de stock de fábrica)',
        items: items.filter(i => selected.includes(i.id)).map(i => ({ materialId: i.id, quantity: Math.max(1, Math.ceil(i.suggestedQuantity)) })),
      });
      setSelected([]);
      setMessage('Pedido interno de compra creado.');
    } catch (e) {
      setError(errorMessage(e, 'No se pudo crear el pedido'));
    }
  };

  return (
    <>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>El aviso es el punto de pedido: por debajo, la materia prima pasa a amarillo.</Typography>
        <Button variant="contained" disabled={selected.length === 0} onClick={request}>Pedir compra ({selected.length})</Button>
      </Stack>
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)} action={<Button color="inherit" size="small" onClick={() => navigate('/purchase-requests')}>Ver pedidos</Button>}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Estado</TableCell>
                <TableCell>Materia prima</TableCell>
                <TableCell align="right">Stock</TableCell>
                <TableCell align="right">Aviso</TableCell>
                <TableCell align="right">Comprar</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(i => (
                <TableRow key={i.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={selected.includes(i.id)} onChange={e => setSelected(e.target.checked ? [...selected, i.id] : selected.filter(x => x !== i.id))} />
                  </TableCell>
                  <TableCell><StatusChip map={LEVEL_CHIP} value={i.level} /></TableCell>
                  <TableCell>{i.name}</TableCell>
                  <TableCell align="right"><b>{fmtQty(i.stock)}</b> {i.unit || ''}</TableCell>
                  <TableCell align="right">{i.warningLevel ? fmtQty(i.warningLevel) : '—'}</TableCell>
                  <TableCell align="right">{i.suggestedQuantity > 0 ? fmtQty(i.suggestedQuantity) : '—'}</TableCell>
                  <TableCell align="right"><IconButton size="small" onClick={() => setEditing(i)}><Edit fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      {editing && <LevelsDialog item={editing} kind="material" onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </>
  );
};

const LevelsDialog: React.FC<{ item: StockStatusItem; kind: 'product' | 'material'; onClose: () => void; onSaved: () => void }> = ({ item, kind, onClose, onSaved }) => {
  const [f, setF] = useState({ min: String(item.minStock || ''), warn: String(item.warningLevel || ''), max: String(item.maxStock || ''), batch: String(item.batchSize || '') });
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    const levels = { minStock: num(f.min), warningLevel: num(f.warn), maxStock: num(f.max), batchSize: num(f.batch) };
    try {
      if (kind === 'product') await api.setProductStockLevels(item.id, levels);
      else await api.setMaterialStockLevels(item.id, levels);
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo guardar'));
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Niveles de {item.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {kind === 'product' && <TextField size="small" type="number" label="Stock mínimo" value={f.min} onChange={e => setF({ ...f, min: e.target.value })} />}
          <TextField size="small" type="number" label="Nivel de aviso" value={f.warn} onChange={e => setF({ ...f, warn: e.target.value })}
            helperText={kind === 'product' ? 'Al llegar acá pasa a amarillo (mayor o igual al mínimo)' : 'Punto de pedido'} />
          {kind === 'product' && (
            <>
              <TextField size="small" type="number" label="Stock máximo" value={f.max} onChange={e => setF({ ...f, max: e.target.value })} helperText="Hasta dónde completar al producir" />
              <TextField size="small" type="number" label="Tanda de producción" value={f.batch} onChange={e => setF({ ...f, batch: e.target.value })} helperText="La sugerencia se redondea a tandas" />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ==================== MP por producto ====================

const UsesTab: React.FC<{ onChange: () => void }> = ({ onChange }) => {
  const [uses, setUses] = useState<MaterialUse[]>([]);
  const [form, setForm] = useState<{ materialId: number | null; productId: number | null; component: string; kg: string }>({ materialId: null, productId: null, component: '', kg: '' });
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => { api.getMaterialUses().then(setUses); }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.materialId || !form.productId) { setError('Elegí materia prima y producto'); return; }
    try {
      await api.createMaterialUse({ materialId: form.materialId, productId: form.productId, component: form.component || null, kgPerUnit: num(form.kg) });
      setForm({ ...form, materialId: null });
      setError(null);
      load();
      onChange();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo agregar'));
    }
  };

  const remove = async (u: MaterialUse) => {
    await api.deleteMaterialUse(u.id);
    load();
    onChange();
  };

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Qué materia prima sirve para cada producto y cuántos kg lleva cada unidad. Con el stock de MP da cuántas unidades se pueden fabricar.
        El componente separa partes con MP distinta (el tapón lleva cuerpo y oring).
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
          <Box sx={{ flex: 1, minWidth: 220 }}><MaterialSelect value={form.materialId} onChange={id => setForm({ ...form, materialId: id })} /></Box>
          <Box sx={{ flex: 1, minWidth: 200 }}><ProductSelect value={form.productId} onChange={id => setForm({ ...form, productId: id })} /></Box>
          <TextField size="small" label="Componente" value={form.component} onChange={e => setForm({ ...form, component: e.target.value })} sx={{ width: 130 }} placeholder="Oring" />
          <TextField size="small" type="number" label="Kg por unidad" value={form.kg} onChange={e => setForm({ ...form, kg: e.target.value })} sx={{ width: 130 }} />
          <Button variant="contained" startIcon={<Add />} onClick={add}>Agregar</Button>
        </Stack>
      </Card>
      <Card variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>Producto</TableCell><TableCell>Componente</TableCell><TableCell>Materia prima</TableCell><TableCell align="right">Kg por unidad</TableCell><TableCell /></TableRow>
          </TableHead>
          <TableBody>
            {uses.length === 0 && <TableRow><TableCell colSpan={5} sx={{ color: 'text.secondary' }}>Sin cargar.</TableCell></TableRow>}
            {uses.map(u => (
              <TableRow key={u.id} hover>
                <TableCell>{u.productName}</TableCell>
                <TableCell>{u.component || '—'}</TableCell>
                <TableCell>{u.materialName}</TableCell>
                <TableCell align="right">{u.kgPerUnit}</TableCell>
                <TableCell align="right"><IconButton size="small" onClick={() => remove(u)}><Delete fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </>
  );
};

export default FactoryStockPage;
